import React, { useEffect, useMemo, useState } from 'react'
import CustomerDetail from './CustomerDetail'
import { Box, Tab, Tabs } from '@mui/material'
import Notes from './tabs/Notes'
import Documents from './tabs/Documents'
import Products from './tabs/Products'
import Deals from './tabs/Deals'
import Tickets from './tabs/Tickets'
import { useParams, useSearchParams } from 'react-router-dom'
import { authedAxios } from '../utils'

import useCustomer from '../hooks/api/useCustomer'
import useBusinessTypes from '../hooks/api/useBusinessTypes'
import useCustomFields from '../hooks/api/useCustomFields'
import useDropdowns from '../hooks/api/useDropdowns'
import useProducts from '../hooks/api/useProducts'
import usePipelines from '../hooks/api/usePipelines'
import useCompanyConfig from '../hooks/api/useCompanyConfig'
import PageTitle from '../common/PageTitle'
import useGenericEndpoint from '../hooks/api/useGenericEndpoint'
import useCustomerContacts from '../hooks/api/useCustomerContacts'
import useCustomerTickets from '../hooks/api/useCustomerTickets'
import History from './tabs/History'
import TabsOrMobileDropdown from '../common/TabsOrMobileDropdown'

const CustomerView = () => {
  const tabs = useMemo(
    () => [
      {
        label: 'Profile',
        Component: CustomerDetail,
      },
      {
        label: 'Notes',
        Component: Notes,
      },
      {
        label: 'Documents',
        Component: Documents,
      },
      // {
      //   label: 'Products',
      //   Component: Products,
      // },
      {
        label: 'Deals',
        Component: Deals,
      },
      {
        label: 'Tickets',
        Component: Tickets,
      },

      {
        label: 'History',
        Component: History,
      },
    ],
    []
  )

  const [tab, setTab] = useState(0)
  const { id } = useParams()
  const isNew = id === 'new'

  const [params] = useSearchParams()
  const ticketId = params.get('ticketId')

  const [types, setTypes] = useState(null)
  const [tasks, setTasks] = useState([])

  const [hasLoadError, setHasLoadError] = useState(false)

  const [typesLoading, setTypesLoading] = useState(true)

  // TECH-DEBT: go through all of these hook calls and see which ones actually need to be in the root
  // My guess is that probably none of them ~should~ be here and most of them can be moved lower
  const { businessTypes, ..._businessTypes } = useBusinessTypes()
  const {
    customFields,
    types: customFieldTypes,
    ..._customFields
  } = useCustomFields()

  const { companyConfig, ..._companyConfig } = useCompanyConfig()

  const { dropdownsSummary: dropdowns, ..._dropdowns } = useDropdowns({
    noFallback: true,
  })

  const { products, billingCycleTypes, ..._products } = useProducts(
    null,
    false,
    true
  )
  const { pipelines, ..._pipelines } = usePipelines()

  const { data: customerStatusTypesData, ..._customerStatusTypes } =
    useGenericEndpoint('/api/customer/types/status')

  const customerQuery = useCustomer(id)
  const customerTicketsQuery = useCustomerTickets(id)
  const customerContactsQuery = useCustomerContacts(id)

  const isLoading =
    _businessTypes.isLoading ||
    _customFields.isLoading ||
    _dropdowns.isLoading ||
    _products.isLoading ||
    _pipelines.isLoading ||
    _companyConfig.isLoading ||
    customerQuery.isLoading ||
    _customerStatusTypes.isLoading ||
    typesLoading

  // TECH-DEBT: I hate this. It's also calling twice along with getCustomer
  const getAllTypes = () => {
    if (types) return
    return authedAxios
      .get('/api/customer/types/all')
      .then((res) => {
        setTypes(res.data.types)
        setTypesLoading(false)
        return res
      })
      .catch((e) => {
        // TODO
        setTypesLoading(false)
      })
  }

  const getTasks = () => {
    return authedAxios.get(`/api/task?customer_id=${id}`).then((res) => {
      setTasks(res.data.tasks)
    })
  }

  const { Component: TabComponent } = tabs[tab]

  useEffect(() => {
    setTab(0)
  }, [id])

  useEffect(() => {
    getAllTypes()
    getTasks()
  }, [])

  useEffect(() => {
    if (ticketId) {
      const targetTab = tabs.find((t) => t.label === 'Tickets')
      const index = tabs.indexOf(targetTab)
      if (index) setTab(index)
    }
  }, [ticketId])

  return (
    <Box>
      <PageTitle>Account Profile</PageTitle>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: 'min(1250px, 90%)' }}>
          {!isNew && (
            <TabsOrMobileDropdown tab={tab} setTab={setTab} tabs={tabs} />
          )}

          <TabComponent
            tasks={tasks}
            isNew={isNew}
            id={id}
            loading={isLoading}
            dropdowns={dropdowns}
            products={products}
            pipelines={pipelines}
            businessTypes={businessTypes}
            types={types}
            customFields={customFields}
            customFieldTypes={customFieldTypes}
            hasLoadError={hasLoadError}
            billingCycleTypes={billingCycleTypes}
            setTab={setTab}
            tabs={tabs}
            customerQuery={customerQuery}
            customerStatusTypesData={customerStatusTypesData}
            customerContactsQuery={customerContactsQuery}
            customerTicketsQuery={customerTicketsQuery}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default CustomerView
